import React, { createContext, useContext, useEffect } from 'react';
import mixpanel from 'mixpanel-browser';
import { PRICE_CATEGORY } from '../setting';

interface MixpanelContextType {
    track: (event: string, data?: Record<string, any>) => void;
}

const MixpanelContext = createContext<MixpanelContextType | undefined>(undefined);

interface MixpanelProviderProps {
    children: React.ReactNode;
    token: string;
}

// Initialize mixpanel synchronously (before any component renders)
let mixpanelInitialized = false;

export const MixpanelProvider: React.FC<MixpanelProviderProps> = ({ children, token }) => {
    // Initialize synchronously on first render
    if (!mixpanelInitialized) {
        mixpanel.init(token, { debug: false });
        mixpanelInitialized = true;
    }

    useEffect(() => {
        trackFirstLaunch()
    }, [token])

    const trackFirstLaunch = () => {
        const key = 'first_launch_tracked';
        const alreadyTracked = localStorage.getItem(key);
        if (alreadyTracked) return;
        track('First Launch: View');
        localStorage.setItem(key, 'true');
    };

    const SENT_EVENTS_KEY = 'mixpanel_sent_events';

    const getSentEvents = (): Set<string> => {
        try {
            const stored = localStorage.getItem(SENT_EVENTS_KEY);
            return stored ? new Set(JSON.parse(stored)) : new Set();
        } catch {
            return new Set();
        }
    };

    const markEventAsSent = (event: string) => {
        const sentEvents = getSentEvents();
        sentEvents.add(event);
        localStorage.setItem(SENT_EVENTS_KEY, JSON.stringify(Array.from(sentEvents)));
    };

    const track = (event: string, data?: Record<string, any>) => {
        // Check if event was already sent
        if (getSentEvents().has(event)) {
            console.log("track skipped (already sent):", event);
            return;
        }

        if (data) {
            data["url_prefix"] = PRICE_CATEGORY
        } else {
            data = {
                "url_prefix": PRICE_CATEGORY
            }
        }
        mixpanel.track(`<LangArena> ${event}`, data);
        markEventAsSent(event);
        console.log("track", event, data)
    }

    return (
        <MixpanelContext.Provider value={{ track }}>
            {children}
        </MixpanelContext.Provider>
    );
};

export const useMixpanel = (): MixpanelContextType => {
    const context = useContext(MixpanelContext);
    if (!context) {
        throw new Error('useMixpanel must be used within a MixpanelProvider');
    }
    return context;
};